import { useState, useEffect } from 'react';
import { Box, Text, Loader, Center, Alert, Stack, Title, Group, SegmentedControl, Card } from '@mantine/core';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, ResponsiveContainer,
} from 'recharts';
import { IconAlertCircle } from '@tabler/icons-react';
import { api } from '../../apiClient';

const TOP_N = 8;

const SERVICE_COLORS = [
  '#228be6', '#12b886', '#40c057', '#fab005', '#fd7e14',
  '#fa5252', '#e64980', '#be4bdb', '#7950f2', '#1c7ed6',
  '#0ca678', '#2f9e44',
];
const OTHER_COLOR = '#868e96';

function CostDashboard() {
  const [rawData, setRawData] = useState(null);
  const [months, setMonths] = useState('6');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get('/admin/costs', { months })
      .then(setRawData)
      .catch(() => setError('コストデータの取得に失敗しました。'))
      .finally(() => setLoading(false));
  }, [months]);

  if (loading) return <Center h={300}><Loader /></Center>;
  if (error) return (
    <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">{error}</Alert>
  );

  const { data: rawRows, services: allServices } = rawData;

  // サービス別合計コストを計算してソート（降順）
  const serviceTotals = {};
  for (const svc of allServices) {
    serviceTotals[svc] = rawRows.reduce((s, row) => s + (row[svc] ?? 0), 0);
  }
  const sortedServices = [...allServices].sort((a, b) => serviceTotals[b] - serviceTotals[a]);
  const topServices = sortedServices.slice(0, TOP_N);
  const otherServices = sortedServices.slice(TOP_N);

  // チャートデータ（その他を集約、合計・ラベル用0値を付加）
  const chartData = rawRows.map(row => {
    const entry = { month: row.month };
    for (const svc of topServices) entry[svc] = row[svc] ?? 0;
    entry['その他'] = otherServices.reduce((s, svc) => s + (row[svc] ?? 0), 0);
    entry._total = topServices.reduce((s, svc) => s + (row[svc] ?? 0), 0) + entry['その他'];
    entry._zero = 0;
    return entry;
  });

  // 今月のデータ
  const currentMonthData = chartData[chartData.length - 1];
  const currentMonth = currentMonthData?.month ?? '';
  const currentMonthCost = currentMonthData?._total ?? 0;

  // recharts は先に定義した Bar が底に積み上げられるため、高コスト順に並べる（降順=底から）
  const series = [
    ...topServices.map((name, i) => ({ name, color: SERVICE_COLORS[i % SERVICE_COLORS.length] })),
    { name: 'その他', color: OTHER_COLOR },
  ];

  // バー上部に合計ラベルを描画するカスタムレンダラー
  const renderTotalLabel = (props) => {
    const { x, y, width, index } = props;
    const total = chartData[index]?._total;
    if (!total || total < 0.01) return null;
    return (
      <text
        x={x + width / 2}
        y={y - 4}
        textAnchor="middle"
        fontSize={10}
        fill="var(--mantine-color-dimmed)"
      >
        ${total.toFixed(2)}
      </text>
    );
  };

  return (
    <Stack gap="lg">
      {/* 今月の利用料 */}
      <Card withBorder radius="md" p="lg" style={{ maxWidth: 300 }}>
        <Text size="sm" c="dimmed">今月の利用料（{currentMonth}）</Text>
        <Group align="baseline" gap="xs" mt={4}>
          <Text size="xl" fw={700}>${currentMonthCost.toFixed(2)}</Text>
          <Text size="sm" c="dimmed">USD</Text>
        </Group>
      </Card>

      {/* タイトル + 表示期間セレクター */}
      <Group justify="space-between" align="center">
        <Title order={4}>月別利用料（サービス別）</Title>
        <SegmentedControl
          value={months}
          onChange={setMonths}
          data={[
            { value: '3', label: '3ヶ月' },
            { value: '6', label: '6ヶ月' },
            { value: '12', label: '12ヶ月' },
          ]}
          size="xs"
        />
      </Group>

      <Box>
        <ResponsiveContainer width="100%" height={420}>
          <BarChart data={chartData} margin={{ top: 24, right: 16, bottom: 80, left: 16 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis tickFormatter={(v) => `$${v}`} width={60} />
            <Tooltip
              content={({ payload, label }) => {
                if (!payload?.length) return null;
                const visible = payload.filter(p => p.dataKey !== '_zero' && (p.value ?? 0) > 0);
                const total = visible.reduce((s, p) => s + (p.value ?? 0), 0);
                return (
                  <Box
                    p="xs"
                    style={{
                      background: 'var(--mantine-color-body)',
                      border: '1px solid var(--mantine-color-default-border)',
                      borderRadius: 4,
                      fontSize: 12,
                      maxHeight: 320,
                      overflowY: 'auto',
                    }}
                  >
                    <Text fw={700} mb={4}>{label}</Text>
                    {[...visible].reverse().map(p => (
                      <Text key={p.dataKey} size="xs" style={{ color: p.fill }}>
                        {p.dataKey}: ${Number(p.value).toFixed(2)}
                      </Text>
                    ))}
                    <Text fw={600} mt={4} size="xs">合計: ${total.toFixed(2)}</Text>
                  </Box>
                );
              }}
            />
            <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: 16 }} />

            {/* 高コスト順（降順）に定義 → 底から積み上げ */}
            {series.map(({ name, color }) => (
              <Bar key={name} dataKey={name} stackId="stack" fill={color} />
            ))}

            {/* 合計ラベル用の高さ0の透明バー（最上部に配置） */}
            <Bar dataKey="_zero" stackId="stack" fill="transparent" isAnimationActive={false} legendType="none">
              <LabelList content={renderTotalLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Stack>
  );
}

export default CostDashboard;
